import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ReportGenerator = ({ onReportGenerated }) => {
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [reportConfig, setReportConfig] = useState({
    name: '',
    description: '',
    dateRange: 'current-month',
    customDateFrom: '',
    customDateTo: '',
    departments: [],
    includeSections: [],
    format: 'pdf',
    currency: 'USD',
    includeCharts: true,
    includeComparative: false,
    confidentialityLevel: 'internal'
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationStep, setGenerationStep] = useState(0);

  const reportTemplates = [
    {
      id: 'monthly-financial-package',
      name: 'Monthly Financial Package',
      description: 'Complete monthly financial statements with analysis',
      icon: 'FileText',
      sections: ['Income Statement', 'Balance Sheet', 'Cash Flow', 'Budget Variance'],
      estimatedTime: '5-7 minutes'
    },
    {
      id: 'executive-summary',
      name: 'Executive Summary',
      description: 'High-level financial overview for leadership',
      icon: 'TrendingUp',
      sections: ['Key Metrics', 'Revenue Analysis', 'Profit Summary', 'Outlook'],
      estimatedTime: '2-3 minutes'
    },
    {
      id: 'budget-variance-report',
      name: 'Budget Variance Report',
      description: 'Detailed budget vs actual analysis',
      icon: 'Target',
      sections: ['Budget Overview', 'Variance Analysis', 'Department Breakdown', 'Action Items'],
      estimatedTime: '3-4 minutes'
    },
    {
      id: 'cash-flow-analysis',
      name: 'Cash Flow Analysis',
      description: 'Comprehensive cash flow reporting and forecasting',
      icon: 'CreditCard',
      sections: ['Cash Position', 'Flow Analysis', 'Forecasting', 'Working Capital'],
      estimatedTime: '4-5 minutes'
    },
    {
      id: 'compliance-package',
      name: 'Compliance Package',
      description: 'Regulatory compliance and audit preparation',
      icon: 'Shield',
      sections: ['Audit Trail', 'Compliance Checklist', 'Risk Assessment', 'Documentation'],
      estimatedTime: '8-10 minutes'
    },
    {
      id: 'custom-report',
      name: 'Custom Report',
      description: 'Build your own report with selected components',
      icon: 'Settings',
      sections: ['Flexible Configuration'],
      estimatedTime: 'Variable'
    }
  ];

  const dateRangeOptions = [
    { value: 'current-month', label: 'Current Month' },
    { value: 'last-month', label: 'Last Month' },
    { value: 'current-quarter', label: 'Current Quarter' },
    { value: 'last-quarter', label: 'Last Quarter' },
    { value: 'current-year', label: 'Current Year' },
    { value: 'last-year', label: 'Last Year' },
    { value: 'custom', label: 'Custom Range' }
  ];

  const departmentOptions = [
    { value: 'sales', label: 'Sales' },
    { value: 'marketing', label: 'Marketing' },
    { value: 'operations', label: 'Operations' },
    { value: 'rd', label: 'R&D' },
    { value: 'hr', label: 'Human Resources' },
    { value: 'finance', label: 'Finance' },
    { value: 'it', label: 'Information Technology' }
  ];

  const formatOptions = [
    { value: 'pdf', label: 'PDF Document', icon: 'FileText' },
    { value: 'excel', label: 'Excel Spreadsheet', icon: 'Table' },
    { value: 'powerpoint', label: 'PowerPoint Presentation', icon: 'Presentation' },
    { value: 'web', label: 'Web Dashboard', icon: 'Globe' }
  ];

  const handleTemplateSelect = (templateId) => {
    const template = reportTemplates?.find(t => t?.id === templateId);
    setSelectedTemplate(templateId);
    setReportConfig(prev => ({
      ...prev,
      name: template?.name || '',
      description: template?.description || '',
      includeSections: template?.sections || []
    }));
  };

  const handleConfigChange = (key, value) => {
    setReportConfig(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleDepartmentToggle = (department) => {
    setReportConfig(prev => ({
      ...prev,
      departments: prev?.departments?.includes(department)
        ? prev?.departments?.filter(d => d !== department)
        : [...prev?.departments, department]
    }));
  };

  const handleSectionToggle = (section) => {
    setReportConfig(prev => ({
      ...prev,
      includeSections: prev?.includeSections?.includes(section)
        ? prev?.includeSections?.filter(s => s !== section)
        : [...prev?.includeSections, section]
    }));
  };

  const generateReport = async () => {
    setIsGenerating(true);
    setGenerationStep(0);

    // Simulate report generation steps
    const steps = [
      'Preparing data sources...',
      'Processing financial data...',
      'Generating charts and graphs...',
      'Applying formatting...',
      'Finalizing report...'
    ];

    for (let i = 0; i < steps?.length; i++) {
      setGenerationStep(i);
      await new Promise(resolve => setTimeout(resolve, 1500));
    }

    setIsGenerating(false);
    setGenerationStep(0);

    // Notify parent component
    onReportGenerated?.({
      ...reportConfig,
      template: selectedTemplate,
      generatedAt: new Date()?.toISOString(),
      id: `report-${Date.now()}`
    });
  };

  const getSelectedTemplate = () => {
    return reportTemplates?.find(t => t?.id === selectedTemplate);
  };

  if (isGenerating) {
    const steps = [
      'Preparing data sources...',
      'Processing financial data...',
      'Generating charts and graphs...',
      'Applying formatting...',
      'Finalizing report...'
    ];

    return (
      <div className="bg-card border border-border rounded-lg p-8 card-shadow">
        <div className="text-center space-y-6">
          <div className="w-16 h-16 bg-primary/10 rounded-lg flex items-center justify-center mx-auto">
            <Icon name="Settings" size={32} className="text-primary animate-spin" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-foreground mb-2">Generating Report</h3>
            <p className="text-muted-foreground">{steps?.[generationStep]}</p>
          </div>
          <div className="w-full max-w-md mx-auto">
            <div className="w-full bg-muted rounded-full h-2">
              <div 
                className="bg-primary h-2 rounded-full transition-all duration-500"
                style={{ width: `${((generationStep + 1) / steps?.length) * 100}%` }}
              />
            </div>
            <div className="flex justify-between text-xs text-muted-foreground mt-2">
              <span>Step {generationStep + 1} of {steps?.length}</span>
              <span>{Math.round(((generationStep + 1) / steps?.length) * 100)}%</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Template Selection */}
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">Select Report Template</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {reportTemplates?.map((template) => (
            <div
              key={template?.id}
              onClick={() => handleTemplateSelect(template?.id)}
              className={`bg-card border rounded-lg p-6 cursor-pointer transition-all hover:shadow-lg ${
                selectedTemplate === template?.id 
                  ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
              }`}
            >
              <div className="flex items-center space-x-3 mb-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  selectedTemplate === template?.id ? 'bg-primary text-primary-foreground' : 'bg-muted'
                }`}>
                  <Icon name={template?.icon} size={20} />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-foreground">{template?.name}</h4>
                  <p className="text-xs text-muted-foreground">{template?.estimatedTime}</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-3">{template?.description}</p>
              <div className="space-y-1">
                {template?.sections?.slice(0, 3)?.map((section, index) => (
                  <div key={index} className="text-xs text-muted-foreground">
                    • {section}
                  </div>
                ))}
                {template?.sections?.length > 3 && (
                  <div className="text-xs text-muted-foreground">
                    + {template?.sections?.length - 3} more sections
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      {selectedTemplate && (
        <div className="bg-card border border-border rounded-lg p-6 card-shadow">
          <h3 className="text-lg font-semibold text-foreground mb-6">Configure Report</h3>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">Report Name</label>
                <input
                  type="text"
                  value={reportConfig?.name}
                  onChange={(e) => handleConfigChange('name', e?.target?.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">Description</label>
                <textarea
                  value={reportConfig?.description}
                  onChange={(e) => handleConfigChange('description', e?.target?.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">Date Range</label>
                <select
                  value={reportConfig?.dateRange}
                  onChange={(e) => handleConfigChange('dateRange', e?.target?.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {dateRangeOptions?.map((option) => (
                    <option key={option?.value} value={option?.value}>
                      {option?.label}
                    </option>
                  ))}
                </select>
              </div>

              {reportConfig?.dateRange === 'custom' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium text-foreground mb-1 block">From</label>
                    <input
                      type="date"
                      value={reportConfig?.customDateFrom}
                      onChange={(e) => handleConfigChange('customDateFrom', e?.target?.value)}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground mb-1 block">To</label>
                    <input
                      type="date"
                      value={reportConfig?.customDateTo}
                      onChange={(e) => handleConfigChange('customDateTo', e?.target?.value)}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Advanced Options */}
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">Output Format</label>
                <div className="grid grid-cols-2 gap-2">
                  {formatOptions?.map((format) => (
                    <label key={format?.value} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        name="format"
                        value={format?.value}
                        checked={reportConfig?.format === format?.value}
                        onChange={(e) => handleConfigChange('format', e?.target?.value)}
                        className="w-4 h-4 text-primary focus:ring-primary"
                      />
                      <Icon name={format?.icon} size={16} className="text-muted-foreground" />
                      <span className="text-sm text-foreground">{format?.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">Departments</label>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {departmentOptions?.map((dept) => (
                    <label key={dept?.value} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={reportConfig?.departments?.includes(dept?.value)}
                        onChange={() => handleDepartmentToggle(dept?.value)}
                        className="w-4 h-4 text-primary focus:ring-primary rounded"
                      />
                      <span className="text-sm text-foreground">{dept?.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={reportConfig?.includeCharts}
                    onChange={(e) => handleConfigChange('includeCharts', e?.target?.checked)}
                    className="w-4 h-4 text-primary focus:ring-primary rounded"
                  />
                  <span className="text-sm text-foreground">Include Charts & Graphs</span>
                </label>

                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={reportConfig?.includeComparative}
                    onChange={(e) => handleConfigChange('includeComparative', e?.target?.checked)}
                    className="w-4 h-4 text-primary focus:ring-primary rounded"
                  />
                  <span className="text-sm text-foreground">Include Comparative Analysis</span>
                </label>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
              <Icon name="Clock" size={16} />
              <span>Estimated time: {getSelectedTemplate()?.estimatedTime}</span>
            </div>
            
            <div className="flex items-center space-x-3">
              <Button variant="outline" onClick={() => setSelectedTemplate('')}>
                Back to Templates
              </Button>
              <Button 
                variant="default" 
                onClick={generateReport}
                disabled={!reportConfig?.name || !selectedTemplate}
                iconName="Play"
              >
                Generate Report
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportGenerator;